/**
 * Implementation primitives for the dynamic per-sensor **track-tail**
 * layer — source + line layer, km-trimming, per-producer
 * FeatureCollection, health-keyed colour (issue #278 follow-up, PR1).
 * Split from :file:`track-tail-layer.tsx` per the
 * ``react-refresh/only-export-components`` rule; mirrors
 * :file:`trail-layer-impl.ts` + :file:`snapshot-layer-impl.ts`.
 *
 * Unlike the single-producer selected trail, this layer renders ALL
 * sensors' recent tails in one line layer (one ``LineString`` feature per
 * producer), each trimmed to the configured km (cumulative great-circle
 * length) and coloured by that producer's latest ``health_state`` via a
 * ``match`` paint expression — so a degraded sensor's tail reads degraded,
 * matching the marker + selected trail. The visual default (2 px, 0.8
 * opacity, round joins) tracks ADR-016 Open Item 12 (still OPEN).
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering precedent) / REQ-UI-040 / REQ-UI-046.
 *   - ADR-016 §5 + Open Item 12.
 *   - map/trail-eligibility.ts (haversineDistanceM) · map/marker-styling.ts.
 */
import type { ExpressionSpecification, GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";
import { toUnwrappedCoordinates } from "@/map/antimeridian";
import { HEALTH_STATE, getStatusColor } from "@/map/marker-styling";
import { isMapUsable } from "@/map/map-ready";
import { SNAPSHOT_LAYER_ID } from "@/map/snapshot-layer-impl";
import { haversineDistanceM } from "@/map/trail-eligibility";
import type { TrackPoint } from "@/map/track-store";

export const TRACK_TAIL_SOURCE_ID = "meridian-track-tail-source";
export const TRACK_TAIL_LAYER_ID = "meridian-track-tail-layer";

/** Tail width in screen px. Matches the selected-trail default. */
export const TRACK_TAIL_LINE_WIDTH_PX = 2;
/** Tail opacity (< 1 so the marker on top reads as current state). */
export const TRACK_TAIL_LINE_OPACITY = 0.8;

/**
 * Trim a producer's positions to the most-recent suffix whose cumulative
 * great-circle length is ``<= km``. Points are oldest-first; the suffix
 * ends at the newest point (the marker position). Returns the input
 * unchanged when it has 0–1 points.
 */
export function trimTrackToKm(points: readonly TrackPoint[], km: number): readonly TrackPoint[] {
  if (points.length <= 1) {
    return points;
  }
  const maxM = km * 1000;
  let cumulative = 0;
  let startIdx = points.length - 1; // always keep the newest point
  for (let i = points.length - 1; i > 0; i -= 1) {
    cumulative += haversineDistanceM(points[i - 1], points[i]);
    if (cumulative > maxM) {
      break;
    }
    startIdx = i - 1;
  }
  return points.slice(startIdx);
}

/** Options for {@link tracksToFeatureCollection}. */
export interface TracksToFeatureCollectionOptions {
  /** Configured tail length (km) — each track trimmed to this. */
  readonly lengthKm: number;
}

/**
 * Build the GeoJSON source payload — one ``LineString`` per producer whose
 * km-trimmed track has >= 2 points, carrying ``producer_id`` +
 * ``health_state`` (latest point) for the paint expression. Every producer
 * (including the selected one) gets a tail so the selected platform's track
 * keeps updating live.
 */
export function tracksToFeatureCollection(
  byProducer: Record<string, readonly TrackPoint[]>,
  options: TracksToFeatureCollectionOptions,
): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  for (const [producerId, points] of Object.entries(byProducer)) {
    // The selected producer's tail is NOT suppressed: it must keep updating
    // live as the platform moves (issue: selected-platform track froze while
    // the marker advanced). The static REQ-UI-012 trail (TrailLayer) stands
    // down while the live dynamic track-tail feature is enabled, so there's
    // no double line — see trail-layer.tsx.
    const trimmed = trimTrackToKm(points, options.lengthKm);
    if (trimmed.length < 2) {
      continue;
    }
    const latest = trimmed[trimmed.length - 1];
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        // Antimeridian-unwrapped so a tail crossing the ±180° seam stays
        // continuous instead of drawing across the whole map (REQ-UI-012).
        coordinates: toUnwrappedCoordinates(trimmed),
      },
      properties: { producer_id: producerId, health_state: latest.health_state },
    });
  }
  return { type: "FeatureCollection", features };
}

/**
 * ``line-color`` ``match`` expression keyed on the per-feature
 * ``health_state``, resolved through the shared marker-status palette
 * (theme-aware on the next paint). Flat literal so TypeScript infers the
 * variadic ``match`` tuple (the snapshot-layer pattern).
 */
function composeTrackColorExpression(): ExpressionSpecification {
  return [
    "match",
    ["get", "health_state"],
    HEALTH_STATE.HEALTHY,
    getStatusColor("healthy"),
    HEALTH_STATE.DEGRADED,
    getStatusColor("degraded"),
    HEALTH_STATE.OFFLINE,
    getStatusColor("unhealthy"),
    HEALTH_STATE.UNSPECIFIED,
    getStatusColor("unknown"),
    HEALTH_STATE.UNKNOWN,
    getStatusColor("unknown"),
    getStatusColor("unknown"),
  ];
}

/**
 * Register the track-tail source + line layer (idempotent: later calls
 * ``setData`` + re-apply the colour expression). Inserted **below** the
 * snapshot marker layer via ``beforeId`` so markers paint on top.
 */
export function attachTrackTailLayer(
  map: MapLibreMap,
  featureCollection: GeoJSON.FeatureCollection<GeoJSON.LineString>,
): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const existing = map.getSource(TRACK_TAIL_SOURCE_ID) as GeoJSONSource | undefined;
  if (existing) {
    existing.setData(featureCollection);
    if (map.getLayer(TRACK_TAIL_LAYER_ID)) {
      map.setPaintProperty(TRACK_TAIL_LAYER_ID, "line-color", composeTrackColorExpression());
    }
    return;
  }

  map.addSource(TRACK_TAIL_SOURCE_ID, { type: "geojson", data: featureCollection });

  const beforeId = map.getLayer(SNAPSHOT_LAYER_ID) ? SNAPSHOT_LAYER_ID : undefined;
  map.addLayer(
    {
      id: TRACK_TAIL_LAYER_ID,
      type: "line",
      source: TRACK_TAIL_SOURCE_ID,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": composeTrackColorExpression(),
        "line-width": TRACK_TAIL_LINE_WIDTH_PX,
        "line-opacity": TRACK_TAIL_LINE_OPACITY,
      },
    },
    beforeId,
  );
}

/** Tear down the layer + source (layer before source). */
export function detachTrackTailLayer(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(TRACK_TAIL_LAYER_ID)) {
    map.removeLayer(TRACK_TAIL_LAYER_ID);
  }
  if (map.getSource(TRACK_TAIL_SOURCE_ID)) {
    map.removeSource(TRACK_TAIL_SOURCE_ID);
  }
}
